// Admin — Monthly Cycle + conversational Agent.
// Phase 1 orchestration layer: launch the AI-driven cycle, chat with
// the admin agent, review the current proposal.
import { useEffect, useState, useRef } from "react";
import { Bot, Send, Loader2, Sparkles, CheckCircle2, XCircle, RefreshCw, PlayCircle, Clock, Trash2 } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const STATUS_LABELS = {
  analyzing: "🔍 Analyse en cours",
  proposed: "💡 Hypothèses proposées",
  sources_requested: "📚 Sources demandées",
  sources_received: "📥 Sources reçues",
  kms_enriching: "🧠 Enrichissement KMS",
  producing: "🚀 Production",
  producing_failed: "⚠️ Production échouée",
  produced: "✅ Contenu prêt",
  published: "📢 Publié",
  measuring: "📊 Mesure",
  closed: "🔒 Clôturé",
};

const AdminMonthlyCyclePage = () => {
  const [cycles, setCycles] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [decisionBusy, setDecisionBusy] = useState(null);

  const reload = async () => {
    try {
      const r = await adminApi.monthlyCycleList();
      setCycles(r?.items || []);
      if (r?.items?.length) setCurrent(r.items[0]);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  // Poll the "current" cycle while it is in a non-terminal status.
  useEffect(() => {
    if (!current?.cycle_id) return;
    if (!["analyzing", "producing", "kms_enriching"].includes(current.status)) return;
    const t = setInterval(async () => {
      const d = await adminApi.monthlyCycleGet(current.cycle_id);
      setCurrent(d);
      setCycles((prev) => prev.map((c) => (c.cycle_id === d.cycle_id ? d : c)));
    }, 4000);
    return () => clearInterval(t);
  }, [current?.cycle_id, current?.status]);

  const launch = async () => {
    setLaunching(true);
    try {
      const r = await adminApi.monthlyCycleLaunch();
      await reload();
      const d = await adminApi.monthlyCycleGet(r.cycle_id);
      setCurrent(d);
    } finally { setLaunching(false); }
  };

  const decide = async (action) => {
    if (!current?.cycle_id) return;
    setDecisionBusy(action);
    try {
      await adminApi.monthlyCycleDecide(current.cycle_id, { action });
      const d = await adminApi.monthlyCycleGet(current.cycle_id);
      setCurrent(d);
      await reload();
    } finally { setDecisionBusy(null); }
  };

  const produce = async () => {
    if (!current?.cycle_id) return;
    setDecisionBusy("produce");
    try {
      await adminApi.monthlyCycleProduce(current.cycle_id);
      const d = await adminApi.monthlyCycleGet(current.cycle_id);
      setCurrent(d);
    } finally { setDecisionBusy(null); }
  };

  if (loading) return <div className="min-h-[40vh] flex items-center justify-center"><Loader2 className="animate-spin text-[color:var(--gv-pink)]" /></div>;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-monthly-cycle">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-1">🤖 Pilotage IA</div>
          <h1 className="font-display text-3xl text-[color:var(--gv-ink)]">Cycle du mois</h1>
          <p className="text-[13px] text-[color:var(--gv-ink)]/60 mt-1 max-w-2xl">
            Boucle : <strong>ANALYSER → PROPOSER → VALIDER → SOURCER → ENRICHIR KMS → PRODUIRE → VALIDER → PUBLIER → MESURER → APPRENDRE</strong>
          </p>
        </div>
        <button onClick={launch} disabled={launching} className="btn-primary" data-testid="cycle-launch">
          {launching ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          🤖 Lancer le cycle du mois
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Timeline */}
        <div className="lg:col-span-1 space-y-2" data-testid="cycle-timeline">
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Historique</div>
          {cycles.length === 0 && <div className="text-[13px] text-[color:var(--gv-ink)]/55">Aucun cycle. Lance le premier avec le bouton ci-dessus.</div>}
          {cycles.map((c) => {
            const isActive = current?.cycle_id === c.cycle_id;
            return (
              <button
                key={c.cycle_id}
                onClick={() => setCurrent(c)}
                className={`w-full text-left p-3 rounded-[5px] border transition-colors ${isActive ? "border-[color:var(--gv-pink)] bg-[color:var(--gv-pink)]/5" : "border-[color:var(--gv-line)] bg-white hover:border-[color:var(--gv-pink)]"}`}
                data-testid={`cycle-item-${c.cycle_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-[11.5px] text-[color:var(--gv-ink)]/70">{c.cycle_id}</span>
                  <span className="text-[10.5px] text-[color:var(--gv-ink)]/50">{(c.created_at || "").slice(0, 10)}</span>
                </div>
                <div className="text-[13.5px] font-medium mt-1 text-[color:var(--gv-ink)] truncate">
                  {c.proposal?.theme || "— thème en cours —"}
                </div>
                <div className="text-[11px] text-[color:var(--gv-ink)]/60 mt-0.5">{STATUS_LABELS[c.status] || c.status}</div>
              </button>
            );
          })}
        </div>

        {/* Current cycle details */}
        <div className="lg:col-span-2 space-y-4">
          {current ? (
            <>
              <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-5" data-testid="cycle-current">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-1">Cycle courant · {current.cycle_id}</div>
                    <div className="font-display text-2xl">{current.proposal?.theme || "— en analyse —"}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] px-2 py-1 rounded bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]/80">
                      {STATUS_LABELS[current.status] || current.status}
                    </span>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Relancer l'analyse du cycle ${current.cycle_id} ? La proposition actuelle sera écrasée.`)) return;
                        try {
                          await adminApi.cycleRestart(current.cycle_id);
                          const d = await adminApi.monthlyCycleGet(current.cycle_id);
                          setCurrent(d);
                        } catch (e) { window.alert(e?.message || String(e)); }
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-[4px] border border-[color:var(--gv-line)] text-[11.5px] text-[color:var(--gv-ink)]/80 hover:bg-stone-50"
                      title="Relancer l'analyse DeepSeek depuis zéro"
                      data-testid="cycle-restart-btn"
                    >
                      <RefreshCw size={11} /> Relancer
                    </button>
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Supprimer définitivement le cycle ${current.cycle_id} ?\nLe bundle lié (si non publié) sera aussi supprimé.`)) return;
                        try {
                          await adminApi.cycleDelete(current.cycle_id);
                          // Reload the list and pick the first non-deleted item
                          const r = await adminApi.monthlyCycleList();
                          const items = r?.items || [];
                          setCycles(items);
                          setCurrent(items[0] || null);
                        } catch (e) { window.alert(e?.message || String(e)); }
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-[4px] border border-rose-200 bg-white text-rose-700 text-[11.5px] hover:bg-rose-50"
                      title="Supprime le cycle (et le bundle non publié)"
                      data-testid="cycle-delete-btn"
                    >
                      <Trash2 size={11} /> Supprimer
                    </button>
                  </div>
                </div>

                {current.status === "analyzing" && (
                  <div className="flex items-center gap-2 text-[13px] text-[color:var(--gv-ink)]/70"><Loader2 className="animate-spin" size={14} /> L'IA analyse le KMS et prépare une proposition…</div>
                )}

                {current.proposal && current.status === "proposed" && (
                  <>
                    <div className="grid grid-cols-2 gap-3 text-[13px] mb-3">
                      <div><span className="text-[color:var(--gv-ink)]/55">Région:</span> <strong>{current.proposal.region}</strong></div>
                      <div><span className="text-[color:var(--gv-ink)]/55">Appellation:</span> <strong>{current.proposal.appellation}</strong></div>
                      <div className="col-span-2"><span className="text-[color:var(--gv-ink)]/55">Angle:</span> {current.proposal.angle}</div>
                      <div className="col-span-2"><span className="text-[color:var(--gv-ink)]/55">Objectif:</span> {current.proposal.objective}</div>
                      <div className="col-span-2"><span className="text-[color:var(--gv-ink)]/55">Opportunité:</span> {current.proposal.opportunity}</div>
                    </div>
                    {Array.isArray(current.proposal.icps) && (
                      <details className="mb-3">
                        <summary className="cursor-pointer text-[12.5px] text-[color:var(--gv-pink)]">Hypothèses par ICP ({current.proposal.icps.length})</summary>
                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                          {current.proposal.icps.map((it) => (
                            <div key={it.icp} className="border border-[color:var(--gv-line)] rounded-[5px] p-2.5 bg-[color:var(--gv-mist)]/30">
                              <div className="flex items-center justify-between mb-1">
                                <strong className="text-[13px]">{it.icp}</strong>
                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[color:var(--gv-pink)] text-[color:var(--gv-pink)]">{it.feature}</span>
                              </div>
                              <div className="text-[11.5px] text-[color:var(--gv-ink)]/80 italic">{it.hypothesis}</div>
                              <div className="text-[11.5px] text-[color:var(--gv-ink)]/70 mt-1">→ {it.cta}</div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => decide("validate")} disabled={!!decisionBusy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[13px] disabled:opacity-40" data-testid="cycle-validate">
                        {decisionBusy === "validate" ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Valider les hypothèses
                      </button>
                      <button onClick={() => decide("regenerate")} disabled={!!decisionBusy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[5px] border border-[color:var(--gv-line)] text-[13px]" data-testid="cycle-regen">
                        {decisionBusy === "regenerate" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Nouvelle proposition
                      </button>
                      <button onClick={() => decide("refuse")} disabled={!!decisionBusy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[5px] border border-red-200 text-red-600 text-[13px]" data-testid="cycle-refuse">
                        <XCircle size={13} /> Refuser
                      </button>
                    </div>
                  </>
                )}

                {current.status === "sources_requested" && (
                  <div className="space-y-3">
                    <SourcesSection cycle={current} onRefresh={async () => {
                      const d = await adminApi.monthlyCycleGet(current.cycle_id);
                      setCurrent(d);
                    }} />
                    <button onClick={produce} disabled={!!decisionBusy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[13px] disabled:opacity-40" data-testid="cycle-produce">
                      {decisionBusy === "produce" ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />} Lancer la production du thème
                    </button>
                  </div>
                )}

                {current.status === "sources_received" && (
                  <div className="space-y-3">
                    <SourcesSection cycle={current} onRefresh={async () => {
                      const d = await adminApi.monthlyCycleGet(current.cycle_id);
                      setCurrent(d);
                    }} />
                    <KmsSection cycle={current} onRefresh={async () => {
                      const d = await adminApi.monthlyCycleGet(current.cycle_id);
                      setCurrent(d);
                    }} />
                    <button onClick={produce} disabled={!!decisionBusy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[13px] disabled:opacity-40" data-testid="cycle-produce">
                      {decisionBusy === "produce" ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />} Lancer la production
                    </button>
                  </div>
                )}

                {current.status === "producing" && current.production_bundle_id && (
                  <div className="flex items-center gap-2 text-[13px] text-[color:var(--gv-ink)]/75">
                    <Loader2 className="animate-spin" size={14} /> Pipeline Thème du mois en cours — bundle <span className="font-mono">{current.production_bundle_id}</span>
                  </div>
                )}

                {["produced", "published", "closed"].includes(current.status) && (
                  <ValidationSection cycle={current} onRefresh={async () => {
                    const d = await adminApi.monthlyCycleGet(current.cycle_id);
                    setCurrent(d);
                  }} />
                )}

                {/* Visuels sociaux — dispo dès qu'une proposition existe */}
                {current.proposal && <SocialSection cycle={current} />}
              </div>

              {/* Agent chat panel */}
              <AgentChat />
            </>
          ) : (
            <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-10 text-center">
              <Bot size={32} className="mx-auto text-[color:var(--gv-pink)]/70 mb-3" />
              <div className="text-[14px] text-[color:var(--gv-ink)]/60">Sélectionne un cycle dans la timeline ou lance-en un nouveau.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────
// Phase 2 — Sources required + upload + KMS proposals
// ──────────────────────────────────────────────────
const SourcesSection = ({ cycle, onRefresh }) => {
  const [requesting, setRequesting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const requested = cycle.sources_requested || [];
  const uploaded = cycle.sources_uploaded || [];

  const ask = async () => {
    setRequesting(true);
    try { await adminApi.cycleSourcesRequest(cycle.cycle_id); await onRefresh(); }
    finally { setRequesting(false); }
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) await adminApi.cycleSourceUpload(cycle.cycle_id, f);
      await onRefresh();
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="border border-[color:var(--gv-line)] rounded-[6px] p-4" data-testid="cycle-sources">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display text-lg">📚 Sources documentaires</div>
        {requested.length === 0 && (
          <button onClick={ask} disabled={requesting} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] border border-[color:var(--gv-line)] rounded disabled:opacity-40" data-testid="cycle-sources-request">
            {requesting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Demander à l'IA
          </button>
        )}
      </div>
      {requested.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {requested.map((s, i) => (
            <div key={i} className="text-[12.5px] flex items-start gap-2">
              <span className={`shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${s.priority === "high" ? "bg-[color:var(--gv-pink)] text-white" : "bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]/70"}`}>{s.priority}</span>
              <div className="flex-1">
                <strong>{s.document}</strong> — {s.information} <span className="text-[color:var(--gv-ink)]/60 italic">({s.kms_target})</span>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2 pt-2 border-t border-[color:var(--gv-line)]">
        <input ref={fileRef} type="file" multiple accept=".pdf,.docx,.txt,.md,.csv" onChange={upload} className="text-[12px]" data-testid="cycle-sources-upload" />
        {uploading && <Loader2 size={13} className="animate-spin text-[color:var(--gv-pink)]" />}
      </div>
      {uploaded.length > 0 && (
        <div className="mt-2 text-[11.5px] text-[color:var(--gv-ink)]/65 space-y-1">
          {uploaded.map((u) => (
            <div key={u.id}>✓ {u.filename} <span className="font-mono">({u.extracted_chars}c)</span></div>
          ))}
        </div>
      )}
    </div>
  );
};


const KmsSection = ({ cycle, onRefresh }) => {
  const [proposing, setProposing] = useState(false);
  const [applying, setApplying] = useState(false);
  const proposals = cycle.kms_proposals || null;

  const propose = async () => {
    setProposing(true);
    try { await adminApi.cycleKmsPropose(cycle.cycle_id); await onRefresh(); }
    finally { setProposing(false); }
  };

  const apply = async () => {
    if (!proposals) return;
    setApplying(true);
    try {
      await adminApi.cycleKmsApply(cycle.cycle_id, {
        appellations: proposals.appellations || [],
        cepages: proposals.cepages || [],
        producers: proposals.producers || [],
      });
      await onRefresh();
    } finally { setApplying(false); }
  };

  if (!proposals) {
    return (
      <div className="border border-[color:var(--gv-line)] rounded-[6px] p-4" data-testid="cycle-kms">
        <div className="flex items-center justify-between">
          <div className="font-display text-lg">🧠 Proposer entrées KMS</div>
          <button onClick={propose} disabled={proposing} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] bg-[color:var(--gv-pink)] text-white rounded disabled:opacity-40" data-testid="cycle-kms-propose">
            {proposing ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Analyser documents
          </button>
        </div>
        <div className="text-[12px] text-[color:var(--gv-ink)]/55 mt-2">L'IA analyse le texte extrait et propose des entrées atlas à valider.</div>
      </div>
    );
  }

  const sections = [
    ["appellations", "Appellations"],
    ["cepages", "Cépages"],
    ["producers", "Producteurs"],
    ["terroirs", "Terroirs (notes)"],
    ["cartes", "Cartes (notes)"],
  ];
  return (
    <div className="border border-[color:var(--gv-line)] rounded-[6px] p-4" data-testid="cycle-kms">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display text-lg">🧠 Entrées KMS proposées</div>
        <button onClick={apply} disabled={applying} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] bg-[color:var(--gv-pink)] text-white rounded disabled:opacity-40" data-testid="cycle-kms-apply">
          {applying ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Valider & écrire au KMS
        </button>
      </div>
      <div className="space-y-2 text-[12.5px]">
        {sections.map(([k, label]) => (
          <div key={k}>
            <div className="font-medium text-[color:var(--gv-ink)]/75">{label} ({(proposals[k] || []).length})</div>
            {(proposals[k] || []).map((item, i) => (
              <div key={i} className="pl-3 py-0.5 text-[color:var(--gv-ink)]/80 text-[12px]">
                • {item.name || item.appellation || "?"} <span className="text-[color:var(--gv-ink)]/50 italic">(source: {item.source_doc || "—"})</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};


// ──────────────────────────────────────────────────
// Phase 4 — Social visuals (Pillow) + ContentStudio push
// ──────────────────────────────────────────────────
const SocialSection = ({ cycle }) => {
  const [generating, setGenerating] = useState(false);
  const [visuals, setVisuals] = useState(null);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await adminApi.cycleSocialVisuals(cycle.cycle_id);
        setVisuals(d);
      } catch { /* no visuals yet */ }
    })();
  }, [cycle.cycle_id]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await adminApi.cycleSocialGenerate(cycle.cycle_id);
      const d = await adminApi.cycleSocialVisuals(cycle.cycle_id);
      setVisuals(d);
    } finally { setGenerating(false); }
  };

  const push = async () => {
    setPushing(true); setPushResult(null);
    try {
      const r = await adminApi.cycleSocialPushContentStudio(cycle.cycle_id);
      setPushResult(r);
    } finally { setPushing(false); }
  };

  const base = process.env.REACT_APP_BACKEND_URL;
  const token = (() => { try { return JSON.parse(localStorage.getItem("geovinum.auth.v1"))?.token; } catch { return null; } })();

  return (
    <div className="border border-[color:var(--gv-line)] rounded-[6px] p-4" data-testid="cycle-social">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="font-display text-lg">📱 Visuels sociaux (rotation 4 langues)</div>
        <div className="flex gap-2">
          <button onClick={generate} disabled={generating} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] border border-[color:var(--gv-line)] rounded disabled:opacity-40" data-testid="cycle-social-generate">
            {generating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Générer / Régénérer
          </button>
          {visuals && (
            <button onClick={push} disabled={pushing} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] bg-[color:var(--gv-pink)] text-white rounded disabled:opacity-40" data-testid="cycle-social-push">
              {pushing ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />} Pousser à ContentStudio
            </button>
          )}
        </div>
      </div>
      {!visuals && <div className="text-[12.5px] text-[color:var(--gv-ink)]/60">Clique "Générer" pour produire 5 visuels (Instagram, LinkedIn, Pinterest, X, Facebook) avec les 4 langues en rotation.</div>}
      {visuals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-2">
          {(visuals.visuals || []).map((v) => (
            <div key={v.platform} className="border border-[color:var(--gv-line)] rounded-[5px] overflow-hidden bg-[color:var(--gv-mist)]">
              {v.image_base64 ? (
                <img src={`data:image/png;base64,${v.image_base64}`} alt={v.platform} className="w-full aspect-square object-contain" />
              ) : (
                <div className="w-full aspect-square flex items-center justify-center text-[color:var(--gv-ink)]/40 text-[11px]">PNG {v.size_bytes}B</div>
              )}
              <div className="p-2 text-center">
                <div className="text-[10.5px] uppercase tracking-wider text-[color:var(--gv-ink)]/60">{v.platform}</div>
                <div className="text-[11px] font-mono text-[color:var(--gv-pink)]">{v.lang?.toUpperCase()}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {pushResult && (
        <div className="mt-2 text-[11.5px] bg-[color:var(--gv-mist)] p-2 rounded">
          {pushResult.status === "not_configured" ? pushResult.message :
            <pre className="whitespace-pre-wrap font-mono text-[10.5px]">{JSON.stringify(pushResult.results || pushResult, null, 2).slice(0, 500)}</pre>
          }
        </div>
      )}
    </div>
  );
};


// ──────────────────────────────────────────────────
// Phase 6 — Centralized validation & publish
// ──────────────────────────────────────────────────
const ValidationSection = ({ cycle, onRefresh }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const d = await adminApi.cycleDeliverables(cycle.cycle_id);
      setData(d);
    } catch (e) {
      setError(e?.message || String(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [cycle.cycle_id]);

  const handlePublishAll = async () => {
    if (!window.confirm("Publier tous les contenus du cycle (blog + academy + newsletters) ?")) return;
    setPublishing(true);
    setError(null);
    try {
      await adminApi.cyclePublishAll(cycle.cycle_id);
      await onRefresh();
      await reload();
    } catch (e) {
      setError(e?.message || String(e));
    } finally { setPublishing(false); }
  };

  const handleReject = async () => {
    if (!window.confirm("Rejeter ce cycle ? Les brouillons restent mais rien n'est publié.")) return;
    try {
      await adminApi.cycleReject(cycle.cycle_id);
      await onRefresh();
    } catch (e) { setError(e?.message || String(e)); }
  };

  if (loading) return <div className="text-[13px] text-stone-500 py-3"><Loader2 size={13} className="animate-spin inline mr-2" /> Chargement des livrables…</div>;
  if (!data) return null;

  const d = data;
  const sections = [
    { key: "blog_articles",    label: "Articles de blog",     items: d.blog_articles,     render: (a) => <span>{a.title} <span className="text-[10px] text-stone-500 ml-1">{a.language.toUpperCase()}</span></span>, statusKey: "status" },
    { key: "newsletter_drafts", label: "Newsletters (brouillons)", items: d.newsletter_drafts, render: (n) => <span>{n.subject || "—"} <span className="text-[10px] text-stone-500 ml-1">{(n.language || "").toUpperCase()}</span></span>, statusKey: "status" },
    { key: "podcast_episodes",  label: "Podcast épisodes",    items: d.podcast_episodes,  render: (p) => <span>{p.title || "Épisode"} <span className="text-[10px] text-stone-500 ml-1">{(p.language || "").toUpperCase()}</span></span>, statusKey: "status" },
    { key: "social_visuals",    label: "Visuels sociaux (Pillow)", items: d.social_visuals, render: (v) => <span>{v.platform} — {(v.language || "").toUpperCase()}</span>, statusKey: null },
    { key: "icp_prospecting",   label: "ICP prospection B2B", items: d.icp_prospecting,   render: (i) => <span>{i.icp} → <span className="text-[10px] text-stone-500">{(i.product || "").toUpperCase()}</span></span>, statusKey: null },
    { key: "cepages",           label: "Cépages créés dans KMS", items: d.cepages,         render: (c) => <span>{c.name}</span>, statusKey: null },
    { key: "producers",         label: "Producteurs créés dans KMS", items: d.producers,   render: (p) => <span>{p.name}</span>, statusKey: null },
  ];

  return (
    <div className="mt-4 border-t border-[color:var(--gv-line)] pt-4" data-testid="cycle-validation">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-[16px] font-semibold">Validation du cycle</div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleReject}
            disabled={publishing || d.status === "published" || d.status === "closed"}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-[4px] border border-rose-200 bg-white text-rose-700 text-[12.5px] disabled:opacity-40"
            data-testid="cycle-reject-btn"
          >
            <XCircle size={12} /> Rejeter
          </button>
          <button
            onClick={handlePublishAll}
            disabled={publishing || d.status === "published"}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-[4px] bg-[color:var(--gv-pink)] text-white text-[12.5px] disabled:opacity-40"
            data-testid="cycle-publish-all-btn"
          >
            {publishing ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
            {d.status === "published" ? "Déjà publié" : "Tout publier"}
          </button>
        </div>
      </div>

      {d.academy_course && (
        <div className="mb-3 text-[12.5px] bg-stone-50 border border-stone-200 rounded-[4px] px-3 py-2">
          <strong>Academy</strong> · {d.academy_course.title}
          <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${d.academy_course.upstream_published ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
            {d.academy_course.upstream_published ? "upstream OK" : "pending upstream"}
          </span>
        </div>
      )}
      {d.appellation_entry && (
        <div className="mb-3 text-[12.5px] bg-stone-50 border border-stone-200 rounded-[4px] px-3 py-2">
          <strong>Appellation KMS</strong> · {d.appellation_entry.name}
          <span className="ml-2 text-[10px] text-stone-500">#{d.appellation_entry.slug}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sections.filter((s) => (s.items || []).length > 0).map((s) => (
          <div key={s.key} className="border border-[color:var(--gv-line)] rounded-[5px] p-3 bg-white" data-testid={`validation-${s.key}`}>
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-[12.5px] font-semibold">{s.label}</div>
              <div className="text-[10.5px] text-stone-500">{s.items.length} éléments</div>
            </div>
            <ul className="space-y-1">
              {s.items.slice(0, 8).map((it, i) => (
                <li key={i} className="flex items-center justify-between text-[12px]">
                  <span className="truncate flex-1">{s.render(it)}</span>
                  {s.statusKey && (
                    <span className={`ml-2 shrink-0 px-1.5 py-0.5 rounded text-[9.5px] ${it[s.statusKey] === "published" ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                      {it[s.statusKey]}
                    </span>
                  )}
                </li>
              ))}
              {s.items.length > 8 && <li className="text-[11px] text-stone-500 pt-1">+{s.items.length - 8} autres…</li>}
            </ul>
          </div>
        ))}
      </div>

      {error && <div className="mt-3 bg-rose-50 border border-rose-200 rounded-[4px] p-2 text-[12px] text-rose-700">{error}</div>}
    </div>
  );
};


// ──────────────────────────────────────────────────
// Agent chat — conversational control plane
// ──────────────────────────────────────────────────
const AgentChat = () => {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Je suis l'agent IA back-office. Je peux lancer le cycle du mois, régénérer des éléments, segmenter les contacts CRM, lancer Apollo (si clé configurée). Que veux-tu faire ?" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo?.(0, scrollRef.current.scrollHeight); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setBusy(true);
    try {
      const r = await adminApi.adminAgentChat({ message: text, session_id: sessionId });
      if (r?.session_id) setSessionId(r.session_id);
      setMessages((m) => [...m, { role: "assistant", text: r?.reply || "…", tools: r?.tool_calls || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: `Erreur: ${e.message}` }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[color:var(--gv-line)] rounded-[6px] p-4" data-testid="admin-agent-chat">
      <div className="flex items-center gap-2 mb-3">
        <Bot size={16} className="text-[color:var(--gv-pink)]" />
        <div className="font-display text-lg">Agent IA back-office</div>
      </div>
      <div ref={scrollRef} className="max-h-72 overflow-y-auto space-y-2 pr-1 mb-3" data-testid="agent-messages">
        {messages.map((m, i) => (
          <div key={i} className={`${m.role === "user" ? "flex justify-end" : "flex justify-start"}`}>
            <div className={`px-3 py-2 rounded-[6px] max-w-[85%] text-[13.5px] ${m.role === "user" ? "bg-[color:var(--gv-pink)] text-white" : "bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]"}`}>
              {m.text}
              {m.tools?.length ? (
                <div className="text-[11px] opacity-70 mt-1">🔧 {m.tools.map((t) => t.name).join(", ")}</div>
              ) : null}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="px-3 py-2 rounded-[6px] bg-[color:var(--gv-mist)] text-[color:var(--gv-ink)]/70 text-[13px]"><Loader2 size={13} className="animate-spin inline mr-1" /> L'agent réfléchit…</div></div>}
      </div>
      <div className="flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ex. 'Lance le cycle du mois', 'Combien de contacts restaurants ?', 'Régénère le podcast du bundle X'"
          className="flex-1 h-10 px-3 rounded-[5px] border border-[color:var(--gv-line)] text-[13.5px] focus:border-[color:var(--gv-pink)] outline-none"
          data-testid="agent-input"
        />
        <button onClick={send} disabled={busy || !input.trim()} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-[5px] bg-[color:var(--gv-pink)] text-white text-[13px] disabled:opacity-40" data-testid="agent-send">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
        </button>
      </div>
    </div>
  );
};

export default AdminMonthlyCyclePage;
